import {
  QBankStructureLevelCategory,
  type SearchLearningObjectiveStatementsQuery,
} from '@exam-management/graphql';
import type { QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import type { Theme } from '@shared/components';
import dayjs from 'dayjs';

export type LearningObjectiveStatement =
  SearchLearningObjectiveStatementsQuery['qbsSearchLearningObjectiveStatements']['learningObjectiveStatements'][number];

export type QBankStructureLevelItem = QBankStructureLevelItemWithLevel & {
  children?: QBankStructureLevelItem[];
  los?: LearningObjectiveStatement[];
};

export function getLevelItemsForLOS(
  levelItems: QBankStructureLevelItem[],
  losList: LearningObjectiveStatement[],
) {
  // sort first, so we can loop through without nested sorting
  const sortedLevelItems = [...levelItems].sort(
    (a, b) => a.qBankStructureLevel.level - b.qBankStructureLevel.level,
  );

  // create map just for a quick lookup
  const levelItemMap = new Map<string, QBankStructureLevelItem>();
  for (const item of sortedLevelItems) {
    levelItemMap.set(item.id, { ...item, children: [] });
  }

  const result = sortedLevelItems.reduce<QBankStructureLevelItem[]>((acc, levelItem) => {
    const currentItem = levelItemMap.get(levelItem.id);
    if (!currentItem) return acc;

    // Add LOS to chapter level item
    if (currentItem.qBankStructureLevel.category === QBankStructureLevelCategory.Chapter) {
      currentItem.los = losList
        .filter((los) => los.qBankStructureLevelItemId === currentItem.id)
        .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
    }

    // Add to parent if it has one and not at the highest level
    if (
      levelItem.parentLevelItemId &&
      ![QBankStructureLevelCategory.Topic, QBankStructureLevelCategory.SubTopic].includes(
        levelItem.qBankStructureLevel.category,
      )
    ) {
      const parentItem = levelItemMap.get(levelItem.parentLevelItemId);
      if (parentItem?.children) {
        parentItem.children.push(currentItem);
        parentItem.children.sort((a, b) => a.order - b.order);
      }
    } else {
      // Add to result if it's a top-level item
      acc.push(currentItem);
    }

    return acc;
  }, []);

  // remove first level by flattening the first children
  const resultWithoutFirstLevel = result
    .flatMap((item) => item.children ?? [])
    .sort((a, b) => a.order - b.order);
  return resultWithoutFirstLevel;
}

export function getBackgroundColor(category: QBankStructureLevelCategory, theme: Theme) {
  const BACKGROUND_COLOR_BY_LEVEL_CATEGORY = {
    [QBankStructureLevelCategory.Course]: theme.palette.grey[300],
    [QBankStructureLevelCategory.Part]: theme.palette.grey[700],
    [QBankStructureLevelCategory.Subject]: 'hsla(37, 96%, 59%, 1)',
    [QBankStructureLevelCategory.Group]: 'hsla(40, 97%, 65%, 1)',
    [QBankStructureLevelCategory.Chapter]: 'hsla(42, 97%, 77%, 1)',
    [QBankStructureLevelCategory.Topic]: 'hsla(43, 96%, 89%, 1)',
    [QBankStructureLevelCategory.SubTopic]: 'hsla(42, 100%, 96%, 1)',
  };
  return BACKGROUND_COLOR_BY_LEVEL_CATEGORY[category];
}
